import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { range, noop, map } from 'lodash';
import { useBranch } from 'baobab-react/hooks';
import Flex from 'styled-flex-component';
import { Button } from 'theme/Button';
import { Col, Row, Grid } from '@zendeskgarden/react-grid';
import { MD } from '@zendeskgarden/react-typography';
import Icon from 'components/Icon/Icon';
import { setValues } from 'utility/urlUtils';
import { paginationShape } from 'propTypesObjects';
import { variables } from 'theme/variables';
import { PaginationWrapper } from './Pagination.styles';
import PaginationDropdown from './components/PaginationDropdown/PaginationDropdown';

const { custom_silver: customSilver, custom_grey: customGrey } = variables;

function Pagination({
  fetchMore = noop,
  pagination: paginationCursor,
  public: isPublic,
  isDashboard,
}) {
  const [pages, setPages] = useState([]);
  const { pagination = {}, filters = {} } = useBranch(paginationCursor.paths);

  const {
    limit = 5,
    num_pages: numPages = 1,
    page = 1,
    total = 0,
  } = pagination;
  const limitRange = [
    { key: 5, value: 5 },
    { key: 10, value: 10 },
    { key: 50, value: 50 },
    { key: 100, value: 100 },
  ];

  useEffect(() => {
    const ranges = map(range(1, numPages + 1), (v) => ({ key: v, value: v }));
    setPages(ranges);
  }, [numPages]);

  const onChange = useCallback(
    (options = {}) => {
      fetchMore({
        pagination: { limit, page, ...options },
        filters,
        sorting: pagination?.sort,
      });
      setValues({ limit, page, ...filters, ...options });
    },
    [fetchMore, filters, limit, page, pagination?.sort]
  );

  if (!total) {
    return <span />;
  }

  return (
    <PaginationWrapper className="pagination-wrapper" public={isPublic}>
      <Grid>
        <Row
          className="pag-row"
          style={{ height: 50 }}
          justifyContent="between"
        >
          <Col alignCenter size="auto" className="limit-col">
            <Row>
              {total > limit && !isDashboard ? (
                <Col
                  className="description"
                  alignCenter
                  size="auto"
                  style={{ paddingRight: 0 }}
                >
                  <MD noMargin grey>
                    Show
                  </MD>
                </Col>
              ) : null}
              {total > limit && !isDashboard ? (
                <Col alignCenter className="limit-dropdown">
                  <PaginationDropdown
                    onChange={onChange}
                    value={limit}
                    items={limitRange}
                    type="results"
                  />
                </Col>
              ) : null}
              <Col className="description count" alignCenter size="auto">
                <MD noMargin grey>
                  <span className="showing">Showing</span>{' '}
                  {page * limit - limit + 1} to{' '}
                  {limit * page > total ? total : limit * page} of{' '}
                  {numeral(total).format('0,0')}{' '}
                  {total === 1 ? 'Result' : 'Results'}
                </MD>
              </Col>
            </Row>
          </Col>
          {pages && pages.length ? (
            <Col alignCenter size="auto">
              <Row>
                <Col alignCenter size="auto">
                  <Button
                    disabled={page === 1}
                    link
                    color={page === 1 ? customSilver : customGrey}
                    onClick={
                      page > 1 ? () => onChange({ page: page - 1 }) : noop
                    }
                  >
                    <Flex
                      alignCenter
                      style={{ position: 'relative', marginRight: 0 }}
                    >
                      <span style={{ paddingRight: 7, paddingTop: 2 }}>
                        <Icon
                          color={page === 1 ? customSilver : customGrey}
                          icon="icon-chevron-left"
                          fontSize="11px"
                        />
                      </span>
                      <MD
                        noMargin
                        color={page === 1 ? customSilver : customGrey}
                      >
                        Last
                      </MD>
                    </Flex>
                  </Button>
                </Col>

                <Col alignCenter className="pagination-dropdown">
                  <PaginationDropdown
                    onChange={onChange}
                    value={page}
                    items={pages}
                    type="page"
                  />
                </Col>

                <Col alignCenter size="auto">
                  <Button
                    disabled={page >= numPages}
                    link
                    color={customGrey}
                    onClick={() => onChange({ page: page + 1 })}
                  >
                    <Flex
                      alignCenter
                      style={{ position: 'relative', marginRight: 0 }}
                    >
                      <MD noMargin grey>
                        Next
                      </MD>
                      <span style={{ paddingLeft: 7, paddingTop: 2 }}>
                        <Icon
                          color={customGrey}
                          icon="icon-chevron-right"
                          fontSize="11px"
                        />
                      </span>
                    </Flex>
                  </Button>
                </Col>
              </Row>
            </Col>
          ) : null}
        </Row>
      </Grid>
    </PaginationWrapper>
  );
}

Pagination.propTypes = {
  fetchMore: PropTypes.func,
  pagination: paginationShape,
  public: PropTypes.bool,
  isDashboard: PropTypes.bool,
};

export default Pagination;
